import { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { PlusIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { format, parseISO } from "date-fns";

import * as actions from "../../store/actions/index";
import { withRouter } from "../../shared/utility";
import EmptyModal from "../../components/modals/EmptyModal";
import ActionLink from "../../components/controls/ActionLink";
import ActionButton from "../../components/controls/ActionButton";
import PurchaseOrderStatusBadge from "../../components/forms/purchaseOrders/PurchaseOrderStatusBadge";
import { usePurchaseOrdersViewModel } from "../../viewmodels/purchaseOrders/usePurchaseOrdersViewModel";
import {
  canDeletePurchaseOrder,
  canReceivePurchaseOrder,
} from "../../components/forms/purchaseOrders/PurchaseOrderStatusBadge";
import { formatCurrency } from "../../shared/utility";
import DateRangeFilter from "../../components/filterDropdown/DateRangeFilter";
import { formatDateRangeSummary } from "../../shared/filterUtils";
import AuditActionLink from "../../components/audit/AuditActionLink";
import AuditHistoriesLink from "../../components/audit/AuditHistoriesLink";
import OperationalDataTableSkeleton from "../../components/operational/OperationalDataTableSkeleton";

function formatPoDate(raw) {
  if (!raw) return "—";
  try {
    return format(parseISO(String(raw).slice(0, 10)), "MMM d, yyyy");
  } catch {
    return String(raw);
  }
}

function PurchaseOrders(props) {
  const { logout, addSnackbar } = props;
  const navigate = useNavigate();
  const vm = usePurchaseOrdersViewModel(logout, addSnackbar);
  const { filters, setFilters, showDates, setShowDates, statusFilter } = vm;
  const searchRef = useRef();

  useEffect(() => {
    vm.getPurchaseOrders("", 1, "");
  }, []);

  const handleSearch = () => {
    vm.getPurchaseOrders(searchRef.current?.value ?? "", 1, statusFilter);
  };

  const rangeSummary = formatDateRangeSummary(
    filters.startDate,
    filters.endDate,
  );
  const rows = vm.purchaseOrders?.data || [];

  return (
    <div className="min-h-screen bg-[#F6F8FB] px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-5">
        <section className="rounded-[18px] border border-[#E2E8F0] bg-white shadow-sm">
          <div className="border-b border-[#E2E8F0] px-5 py-5 sm:px-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="max-w-3xl">
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#64748B]">
                  QA Trace / Purchasing
                </p>
                <h1 className="mt-1 text-[1.65rem] font-semibold tracking-tight text-[#0F172A] sm:text-3xl">
                  Purchase orders
                </h1>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-[#64748B]">
                  Create, track, approve, and receive supplier purchase orders
                  before materials enter inventory.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <AuditHistoriesLink slug="purchase-order" />
                <button
                  type="button"
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-[#2563EB] bg-[#2563EB] px-3.5 text-sm font-semibold text-white shadow-sm hover:bg-[#1D4ED8]"
                  onClick={() => navigate("/purchase-orders/new")}
                >
                  <PlusIcon className="h-4 w-4" aria-hidden />
                  Create purchase order
                </button>
              </div>
            </div>
          </div>

          <div className="grid gap-3 px-5 py-4 sm:px-6 lg:grid-cols-[minmax(240px,1fr)_200px_1fr] lg:items-center">
            <div className="flex h-11 overflow-hidden rounded-xl border border-[#E2E8F0] bg-white shadow-sm">
              <span className="inline-flex items-center pl-3 text-[#64748B]">
                <MagnifyingGlassIcon className="h-5 w-5" aria-hidden />
              </span>
              <input
                ref={searchRef}
                type="search"
                className="h-full w-full border-0 bg-transparent px-3 text-sm focus:outline-none"
                placeholder="Search PO # or supplier"
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              <button
                type="button"
                className="border-l border-[#E2E8F0] px-4 text-sm font-semibold text-[#2563EB]"
                onClick={handleSearch}
              >
                Search
              </button>
            </div>
            <select
              className="h-11 rounded-xl border border-[#E2E8F0] bg-white px-3 text-sm"
              value={statusFilter}
              onChange={(e) => {
                vm.getPurchaseOrders(
                  searchRef.current?.value ?? "",
                  1,
                  e.target.value,
                );
              }}
            >
              <option value="">All statuses</option>
              {vm.statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              <DateRangeFilter
                onChange={(startDate, endDate) =>
                  setFilters({
                    ...filters,
                    startDate,
                    endDate,
                  })
                }
                onClear={() =>
                  setFilters({
                    ...filters,
                    startDate: null,
                    endDate: null,
                  })
                }
                startDate={filters.startDate}
                endDate={filters.endDate}
                showDates={showDates}
                setShowDates={setShowDates}
                applyChanges={handleSearch}
                maxDate={new Date()}
                rangeSummary={rangeSummary}
              />
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-[18px] border border-[#E2E8F0] bg-white shadow-sm">
          {vm.loading ? (
            <OperationalDataTableSkeleton
              columns={[
                "PO #",
                "Supplier",
                "Expected delivery",
                "Status",
                "Ordered total",
                "Received",
                "Actions",
              ]}
              rows={8}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E2E8F0] bg-[#EFF6FF] text-left text-[11px] font-semibold uppercase tracking-wide text-[#64748B]">
                    {[
                      "PO #",
                      "Supplier",
                      "Expected delivery",
                      "Status",
                      "Ordered total",
                      "Received",
                      "Actions",
                    ].map((col) => (
                      <th key={col} className="px-4 py-3">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.length > 0 ? (
                    rows.map((row) => {
                      const id = row.id;
                      const orderedAmount = row.ordered_amount ?? "0";
                      const receivedAmount = row.received_amount ?? "0";
                      return (
                        <tr
                          key={id}
                          className="border-b border-[#E2E8F0]/70 hover:bg-[#F6F8FB]"
                        >
                          <td className="px-4 py-3.5 font-semibold text-[#0F172A]">
                            {row.po_number ?? `PO-${id}`}
                          </td>
                          <td className="px-4 py-3.5 text-[#64748B]">
                            {row.supplier_name ?? row.supplier?.name ?? "—"}
                          </td>
                          <td className="px-4 py-3.5 text-[#64748B]">
                            {formatPoDate(row.expected_delivery_date)}
                          </td>
                          <td className="px-4 py-3.5">
                            <PurchaseOrderStatusBadge status={row.status} />
                          </td>
                          <td className="px-4 py-3.5 tabular-nums">
                            {row.total_cost != null
                              ? formatCurrency(row.total_cost)
                              : row.ordered_total != null
                                ? formatCurrency(row.ordered_total)
                                : "—"}
                          </td>
                          <td className="px-4 py-3.5 tabular-nums text-[#64748B]">
                            {receivedAmount} / {orderedAmount}
                          </td>
                          <td className="px-4 py-3.5">
                            <div className="flex flex-wrap gap-2">
                              <ActionLink to={`/purchase-orders/${id}`}>
                                Edit
                              </ActionLink>
                              <AuditActionLink
                                slug="purchase-order"
                                id={id}
                                name={row.po_number ?? `PO-${id}`}
                              />
                              {canReceivePurchaseOrder(row.status) && (
                                <ActionLink
                                  to={`/purchase-orders/${id}/receive`}
                                >
                                  Receive
                                </ActionLink>
                              )}
                              {canDeletePurchaseOrder(
                                row.status,
                                row.received_amount,
                              ) && (
                                <ActionButton
                                  color="red"
                                  onClick={() => vm.askDeletePurchaseOrder(row)}
                                >
                                  Delete
                                </ActionButton>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-8 text-center text-[#64748B]"
                      >
                        No purchase orders found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <EmptyModal
        childComponent={vm.emptyModal.childComponent}
        open={vm.emptyModal.open}
        setOpen={vm.setEmptyModelOpen}
      />
    </div>
  );
}

const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(actions.logout()),
  addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
});

export default withRouter(connect(null, mapDispatchToProps)(PurchaseOrders));
